import React, { useMemo, useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Star,
  MapPin,
  Users,
  Award,
  GraduationCap,
  CalendarCheck,
  Check,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  X,
  ChevronDown,
  ChevronUp,
  Heart,
  Sparkles,
  BadgeCheck,
  Clock3,
  MessageCircle,
  ShieldCheck,
  UserRoundCheck,
} from "lucide-react";
import Button from "../components/Button";
import MatchBadge from "../components/MatchBadge";
import FilterChip from "../components/FilterChip";
import SchoolCard from "../components/SchoolCard";
import { schools, getSchoolBySlug } from "../data/schools";
import { formatRupiah } from "../lib/format";
import { useCompare } from "../context/CompareContext";

const BADGE_COLORS = [
  "bg-blue-100 text-blue-800 border-blue-200",
  "bg-emerald-100 text-emerald-800 border-emerald-200",
  "bg-purple/10 text-purple border-purple/20",
  "bg-orange-100 text-orange-800 border-orange-200",
  "bg-pink-100 text-pink-800 border-pink-200",
  "bg-teal-100 text-teal-800 border-teal-200",
];

export default function SchoolDetail() {
  const { slug } = useParams();
  const school = getSchoolBySlug(slug);
  const { isInCompare, toggleCompare } = useCompare();

  const [activeImage, setActiveImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [consultationOpen, setConsultationOpen] = useState(false);
  const [consultationTopic, setConsultationTopic] = useState("general");

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && lightboxOpen) setLightboxOpen(false);
      if (e.key === "Escape" && consultationOpen) setConsultationOpen(false);
      if (e.key === "ArrowRight" && lightboxOpen) nextImage();
      if (e.key === "ArrowLeft" && lightboxOpen) prevImage();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, consultationOpen, activeImage]);

  useEffect(() => {
    if (!consultationOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [consultationOpen]);

  const similarSchools = useMemo(() => {
    if (!school) return [];
    const scored = schools.map((s) => {
      if (s.id === school.id) return { school: s, score: -1 };
      let score = 0;
      if (s.city === school.city) score += 3;
      if (s.educationLevels.some((l) => school.educationLevels.includes(l))) score += 2;
      if (s.curriculum === school.curriculum) score += 2;
      if (Math.abs(s.monthlyTuition - school.monthlyTuition) < 1000000) score += 2;
      const sharedTags = s.tags.filter((t) => school.tags.includes(t));
      score += sharedTags.length;
      return { school: s, score };
    });
    return scored
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((s) => s.school);
  }, [school]);

  if (!school) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="text-3xl font-extrabold mb-3">Sekolah tidak ditemukan</h1>
        <p className="text-ink/60 mb-6">Sekolah yang Anda cari mungkin sudah tidak tersedia.</p>
        <Button to="/schools">← Kembali ke daftar sekolah</Button>
      </div>
    );
  }

  const images = useMemo(() => {
    let imgs = school.images || [school.image];
    if (imgs.length < 12) {
      const repeated = [];
      while (repeated.length < 12) {
        repeated.push(...imgs);
      }
      imgs = repeated.slice(0, 12);
    }
    return imgs;
  }, [school]);
  const inCompare = isInCompare(school.slug);
  const schoolContact = {
    name: school.contactPerson?.name || "Nadia Putri",
    role: school.contactPerson?.role || "Admin Penerimaan Siswa",
    photo: school.contactPerson?.photo || "/assets/kepala-sekolah.png",
    whatsapp: school.contactPerson?.whatsapp || school.phone || "+62 812-3456-7890",
    responseTime: school.contactPerson?.responseTime || "Biasanya membalas dalam 15 menit",
  };

  const consultationTopics = [
    {
      id: "general",
      label: "Tanya informasi sekolah",
      icon: MessageCircle,
      message: `Halo Kak ${schoolContact.name}, saya menemukan ${school.name} melalui MySchoolMate. Saya ingin bertanya lebih lanjut mengenai informasi sekolah dan proses pendaftarannya.`,
    },
    {
      id: "tour",
      label: "Jadwalkan school tour",
      icon: CalendarCheck,
      message: `Halo Kak ${schoolContact.name}, saya menemukan ${school.name} melalui MySchoolMate. Saya tertarik untuk melakukan school tour. Boleh diinformasikan jadwal kunjungan yang tersedia?`,
    },
    {
      id: "fees",
      label: "Biaya & pendaftaran",
      icon: GraduationCap,
      message: `Halo Kak ${schoolContact.name}, saya menemukan ${school.name} melalui MySchoolMate. Saya ingin bertanya tentang rincian biaya, persyaratan, dan jadwal pendaftaran siswa baru.`,
    },
  ];
  const selectedTopic = consultationTopics.find((topic) => topic.id === consultationTopic) || consultationTopics[0];

  const openConsultation = (topic = "general") => {
    setConsultationTopic(topic);
    setConsultationOpen(true);
  };

  const openWhatsApp = () => {
    const number = schoolContact.whatsapp.replace(/\D/g, "").replace(/^0/, "62");
    window.open(`https://wa.me/${number}?text=${encodeURIComponent(selectedTopic.message)}`, "_blank", "noopener,noreferrer");
  };

  const nextImage = (e) => {
    if (e) e.stopPropagation();
    setActiveImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    if (e) e.stopPropagation();
    setActiveImage((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
      <div className="mb-6 text-sm flex justify-between items-center">
        <Link to="/schools" className="text-purple font-semibold hover:underline inline-flex items-center gap-1">
          <ChevronLeft size={16} /> Kembali
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,380px] gap-8 xl:gap-12 items-start">
        
        {/* 1. GALLERY */}
        <div className="order-1 lg:col-start-1 lg:row-start-1 space-y-3 w-full max-w-3xl mx-auto lg:max-w-none">
          <div
              className="relative aspect-[16/9] sm:aspect-[21/9] lg:aspect-[16/9] bg-ink/5 rounded-card overflow-hidden group cursor-pointer"
              onClick={() => setLightboxOpen(true)}
            >
              <img
                src={images[activeImage]}
                alt={`${school.name} gallery ${activeImage + 1}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <button
                className="absolute top-4 right-4 bg-ink/80 text-warm p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Perbesar gambar"
              >
                <Maximize2 size={20} />
              </button>
              
              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-cream/90 text-ink p-2 rounded-full hover:bg-warm shadow-sm transition-transform hover:scale-110"
                    aria-label="Gambar sebelumnya"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-cream/90 text-ink p-2 rounded-full hover:bg-warm shadow-sm transition-transform hover:scale-110"
                    aria-label="Gambar selanjutnya"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
            </div>
            
            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="grid grid-cols-7 gap-2 sm:gap-3 w-full pb-1">
                {images.slice(0, 7).map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => idx === 6 ? setLightboxOpen(true) : setActiveImage(idx)}
                    className={`relative w-full aspect-[4/3] rounded-xl overflow-hidden border-2 transition-colors ${
                      idx === activeImage && idx !== 6 ? "border-purple" : "border-transparent opacity-80 hover:opacity-100"
                    }`}
                  >
                    <img onError={(e) => { e.target.style.display = "none"; }} src={img} alt="" className="w-full h-full object-cover" />
                    {idx === 6 && images.length > 6 && (
                      <div className="absolute inset-0 bg-ink/60 flex flex-col items-center justify-center text-white">
                        <span className="font-extrabold text-lg sm:text-xl">+{images.length - 6}</span>
                        <span className="text-[10px] sm:text-xs font-semibold tracking-wide">Lainnya</span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

        {/* 1.5 MOBILE HEADER */}
        <div className="order-2 lg:hidden space-y-4 w-full max-w-3xl mx-auto">
          <h1 className="text-3xl font-extrabold leading-tight">{school.name}</h1>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="inline-flex items-center gap-1 font-bold text-ink bg-yellow/20 px-2 py-0.5 rounded-lg">
              <Star size={16} className="fill-yellow text-yellow" /> {school.rating}
            </span>
            <span className="text-ink/60 font-medium">{school.reviewCount} ulasan</span>
          </div>
          <p className="flex items-center gap-1.5 text-ink/80 font-medium">
            <MapPin size={18} className="text-purple shrink-0" />
            {school.district}, {school.city} • {school.distanceKm} km
          </p>
        </div>

        {/* 3. CARDS (Profil, Kegiatan, Ulasan) */}
        <div className="order-4 lg:col-start-1 lg:row-start-2 space-y-8 w-full max-w-3xl mx-auto lg:max-w-none">

          {/* CARD 1: PROFIL & AKADEMIK */}
          <div className="bg-warm ink-border rounded-card p-6 lg:p-10 shadow-offset-sm relative overflow-hidden">
            {/* Background Decorative Image */}
            <img 
              src="/assets/slug/sekolah1.png" 
              alt="" 
              className="absolute -bottom-20 -right-20 w-[300px] sm:w-[400px] lg:w-[500px] opacity-20 lg:opacity-30 pointer-events-none z-0" 
              aria-hidden="true"
            />
            
            <div className="relative z-10 space-y-10">
              <div className="border-b-4 border-ink pb-4 flex flex-col sm:flex-row sm:items-end justify-between gap-2">
                <h2 className="text-2xl lg:text-3xl font-black text-ink">Profil & Akademik</h2>
                <p className="text-ink/50 font-medium text-xs sm:text-sm">
                  Terakhir diperbarui pada <span className="font-bold">12 Agustus 2026</span>
                </p>
              </div>
              
              <Section title="Overview">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <Fact icon={<GraduationCap />} label="Jenjang" value={school.educationLevels.join(", ")} />
                  <Fact icon={<Award />} label="Akreditasi" value={school.accreditation} />
                  <Fact icon={<Users />} label="Rasio Guru" value={`1:${school.studentTeacherRatio}`} />
                  <Fact icon={<Check />} label="Keb. Khusus" value={school.specialNeedsSupport ? "Tersedia" : "Tidak"} />
                </div>
              </Section>

              <Section title="Tentang Sekolah">
                <p className="text-ink/80 leading-relaxed text-lg">{school.about}</p>
              </Section>

              <Section title="Biaya & Informasi Akademik">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-transparent border border-ink/20 rounded-2xl p-5">
                    <p className="text-sm text-ink/60 font-bold uppercase tracking-wide">SPP Bulanan</p>
                    <p className="text-2xl font-extrabold mt-1 text-purple">{formatRupiah(school.monthlyTuition)}</p>
                  </div>
                  <div className="bg-transparent border border-ink/20 rounded-2xl p-5">
                    <p className="text-sm text-ink/60 font-bold uppercase tracking-wide">Uang Pangkal</p>
                    <p className="text-2xl font-extrabold mt-1 text-ink">{formatRupiah(school.entranceFee)}</p>
                  </div>
                </div>
                <div className="mt-4 p-5 bg-transparent border border-ink/20 rounded-2xl">
                  <p className="text-ink/80 leading-relaxed">
                    Menggunakan kurikulum <strong>{school.curriculum}</strong>. Program pengajaran berfokus pada pengembangan
                    akademik komprehensif yang dirancang untuk mendukung minat bakat anak.
                  </p>
                </div>
              </Section>
            </div>
          </div>

          {/* CARD 2: KEGIATAN & FASILITAS */}
          <div className="bg-warm ink-border rounded-card p-6 lg:p-10 shadow-offset-sm relative overflow-hidden mt-8">
            {/* Background Decorative Image */}
            <img 
              src="/assets/slug/sekolah2.png" 
              alt="" 
              className="absolute -bottom-20 -right-20 w-[300px] sm:w-[400px] lg:w-[500px] opacity-20 lg:opacity-30 pointer-events-none z-0" 
              aria-hidden="true"
            />
            
            <div className="relative z-10 space-y-10">
              <div className="border-b-4 border-ink pb-4">
                <h2 className="text-2xl lg:text-3xl font-black text-ink">Kegiatan & Fasilitas</h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-12 sm:gap-8">
                <Section title="Fasilitas">
                  <ul className="grid sm:grid-cols-1 gap-3">
                  {school.facilities.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-ink/80 font-medium">
                      <Check size={20} className="text-mint shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
              </Section>

              <Section title="Ekstrakurikuler">
                <div className="flex flex-wrap gap-2">
                  {school.extracurriculars.map((e, idx) => (
                    <span key={e} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-sm border ${BADGE_COLORS[idx % BADGE_COLORS.length]}`}>
                      {e}
                    </span>
                  ))}
                </div>
              </Section>
              </div>

              {school.religiousPrograms.length > 0 && (
                <Section title="Program Khusus">
                  <div className="flex flex-wrap gap-2">
                    {school.religiousPrograms.map((p, idx) => (
                      <span key={p} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-sm border ${BADGE_COLORS[(idx + 3) % BADGE_COLORS.length]}`}>
                        {p}
                      </span>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          </div>

          {/* CARD 3: INFORMASI TAMBAHAN */}
          <div className="bg-warm ink-border rounded-card p-6 lg:p-10 shadow-offset-sm space-y-10 mt-8">
            <div className="border-b-4 border-ink pb-4">
              <h2 className="text-2xl lg:text-3xl font-black text-ink">Informasi Tambahan</h2>
            </div>

            <Section title="Kontak & Lokasi">
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div className="flex items-center gap-3 bg-white border border-ink/20 rounded-2xl p-4">
                  <div className="w-10 h-10 bg-warm rounded-xl flex items-center justify-center ink-border shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  </div>
                  <div>
                    <p className="text-xs text-ink/60 font-bold uppercase tracking-wide">Telepon</p>
                    <p className="font-bold">{school.phone || "+62 812-3456-7890"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white border border-ink/20 rounded-2xl p-4">
                  <div className="w-10 h-10 bg-warm rounded-xl flex items-center justify-center ink-border shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                  </div>
                  <div>
                    <p className="text-xs text-ink/60 font-bold uppercase tracking-wide">Email</p>
                    <p className="font-bold">{school.email || `info@${school.slug}.sch.id`}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white border border-ink/20 rounded-2xl p-5">
                <MapPin size={24} className="text-coral shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-lg mb-1">{school.name}</p>
                  <p className="text-ink/80 leading-relaxed">{school.address}</p>
                  <p className="text-ink/60 text-sm mt-2">{school.distanceKm} km dari pusat kota</p>
                </div>
              </div>
            </Section>

            {school.faq && school.faq.length > 0 && (
              <Section title="FAQ">
                <div className="space-y-3">
                  {school.faq.map((item, idx) => (
                    <div key={idx} className="bg-warm border border-ink/20 rounded-2xl overflow-hidden transition-colors">
                      <button
                        onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                        className="w-full px-5 py-4 flex items-center justify-between text-left font-bold hover:bg-cream-2 focus:outline-none"
                      >
                        {item.question}
                        {openFaq === idx ? <ChevronUp size={20} className="text-ink/50" /> : <ChevronDown size={20} className="text-ink/50" />}
                      </button>
                      {openFaq === idx && (
                        <div className="px-5 pb-5 pt-1 text-ink/80 leading-relaxed border-t border-ink/10">
                          {item.answer}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </div>

          {/* CARD 4: ULASAN & TESTIMONI */}
          <div className="bg-warm ink-border rounded-card p-6 lg:p-10 shadow-offset-sm space-y-8 mt-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b-4 border-ink pb-4">
              <h2 className="text-2xl lg:text-3xl font-black text-ink">Ulasan & Testimoni</h2>
              <Button variant="yellow" size="sm" className="whitespace-nowrap w-full sm:w-auto">
                <Star aria-hidden="true" size={16} />
                Berikan Penilaian Anda
              </Button>
            </div>

            <div className="flex items-center gap-4 bg-white border border-ink/20 rounded-2xl p-5">
              <div className="text-4xl font-black text-ink">{school.rating}</div>
              <div>
                <div className="flex text-yellow mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={20} className={i < Math.floor(school.rating) ? "fill-yellow" : "text-ink/20"} />
                  ))}
                </div>
                <p className="text-sm font-bold text-ink/60">Berdasarkan {school.reviewCount} ulasan keluarga</p>
              </div>
            </div>

            <div className="space-y-4">
              {[
                { author: "Bunda Aisha", role: "Orang Tua Siswa SD", quote: "Fasilitasnya sangat bagus dan guru-gurunya ramah. Anak saya sangat senang bersekolah di sini dan perkembangannya luar biasa.", rating: 5 },
                { author: "Ayah Bima", role: "Orang Tua Siswa SMP", quote: "Kurikulum yang diterapkan sangat seimbang antara akademik dan karakter. Ekstrakurikulernya juga beragam.", rating: 4 },
              ].map((review, idx) => (
                <div key={idx} className="bg-white border border-ink/20 rounded-2xl p-5">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple/10 border border-ink/10 flex items-center justify-center font-black text-purple">
                        {review.author.charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-ink">{review.author}</p>
                        <p className="text-xs font-semibold text-ink/50">{review.role}</p>
                      </div>
                    </div>
                    <div className="flex text-yellow">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} size={14} className="fill-yellow text-yellow" />
                      ))}
                    </div>
                  </div>
                  <p className="text-ink/80 text-sm leading-relaxed font-medium">"{review.quote}"</p>
                </div>
              ))}
            </div>

            {/* Pagination Mock */}
            <div className="flex items-center justify-center gap-1 pt-4 border-t border-ink/10">
              <button className="p-2 text-ink/40 hover:text-ink transition-colors" disabled>
                <ChevronLeft size={20} />
              </button>
              {[1, 2, 3, 4, 5].map((page) => (
                <button 
                  key={page}
                  className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold transition-colors ${page === 1 ? 'bg-ink text-white' : 'text-ink/60 hover:bg-ink/5'}`}
                >
                  {page}
                </button>
              ))}
              <span className="px-1 text-ink/40 font-bold">...</span>
              <button className="p-2 text-ink/60 hover:text-ink transition-colors">
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* 2. RIGHT COLUMN */}
        <aside className="order-3 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-24 w-full max-w-3xl mx-auto lg:max-w-none">
          <div className="bg-warm ink-border rounded-card p-6 shadow-offset-sm">
            
            {/* Header Section */}
            <div className="hidden lg:flex flex-col gap-4 mb-6">
              <div className="flex justify-between items-start">
                <img src="/assets/slug/logo-alfalah.png" alt="Logo" className="w-20 h-20 rounded-2xl object-contain bg-white border border-ink/10 shadow-sm shrink-0 p-1" />
                <button className="text-ink/40 hover:text-coral transition-colors shrink-0 mt-2">
                  <Heart size={28} />
                </button>
              </div>
              
              <h1 className="text-3xl lg:text-4xl font-extrabold leading-tight">{school.name}</h1>
              
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 font-bold text-ink bg-yellow/20 px-2 py-1 rounded-lg text-base">
                    <Star size={18} className="fill-yellow text-yellow" /> {school.rating}
                  </span>
                  <span className="text-ink/60 font-medium text-base">{school.reviewCount} ulasan</span>
                </div>
                <p className="flex items-center gap-1.5 text-ink/80 font-medium text-base">
                  <MapPin size={18} className="text-purple shrink-0" />
                  {school.city}
                </p>
              </div>
            </div>

            {/* Pricing Section */}
            <div className="mb-6 grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-extrabold text-ink/60 uppercase tracking-wide">Uang Pangkal</p>
                <div className="flex items-end gap-1 mt-1">
                  <p className="text-xl font-extrabold text-ink leading-none">{formatRupiah(school.entranceFee)}</p>
                </div>
              </div>
              <div>
                <p className="text-xs font-extrabold text-ink/60 uppercase tracking-wide">SPP Bulanan</p>
                <div className="flex items-end gap-1 mt-1">
                  <p className="text-xl font-extrabold text-ink leading-none">{formatRupiah(school.monthlyTuition)}</p>
                </div>
              </div>
            </div>

            {/* Buttons Section */}
            <div className="space-y-3">
              <Button
                onClick={() => toggleCompare(school.slug)}
                variant={inCompare ? "mint" : "primary"}
                className="w-full text-base py-3.5 flex items-center justify-center gap-2"
                data-testid="detail-compare-btn"
              >
                <div className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="M7 21h10"/><path d="M12 3v18"/><path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"/></svg>
                  {inCompare ? "Dalam Perbandingan" : "Bandingkan Sekolah"}
                </div>
              </Button>
              <Button
                onClick={() => openConsultation("general")}
                variant="mint"
                className="w-full py-3.5"
                data-testid="detail-whatsapp-btn"
              >
                <div className="flex items-center gap-2 justify-center w-full">
                  <WhatsAppIcon className="w-[19px] h-[19px]" /> Tanya Sekolah via WhatsApp
                </div>
              </Button>
              {/* Tombol School Tour disembunyikan sementara.
              <Button
                onClick={() => openConsultation("tour")}
                variant="secondary"
                className="w-full py-3.5 bg-cream hover:bg-cream-2"
                data-testid="detail-tour-btn"
              >
                <div className="flex items-center gap-2 justify-center w-full">
                  <CalendarCheck size={18} /> Jadwalkan School Tour
                </div>
              </Button>
              */}
              <Button 
                variant="secondary" 
                className="w-full py-3.5 bg-transparent border-2 border-ink/20 hover:border-ink/40 text-ink"
                onClick={() => window.open(school.website || "https://example.com", "_blank")}
              >
                <div className="flex items-center gap-2 justify-center w-full">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>
                  Kunjungi Situs Sekolah
                </div>
              </Button>
            </div>

            <button
              type="button"
              onClick={() => openConsultation("general")}
              className="mt-5 w-full rounded-2xl border border-ink/15 bg-[#F2FBF8] p-3 text-left transition-all hover:border-ink/30 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-mint/40"
              aria-label={`Lihat profil kontak resmi ${school.name}`}
            >
              <div className="flex items-center gap-3">
                <div className="relative shrink-0">
                  <img
                    src={schoolContact.photo}
                    alt={`Foto ${schoolContact.name}`}
                    className="h-12 w-12 rounded-full border-2 border-white object-cover shadow-sm"
                  />
                  <BadgeCheck className="absolute -bottom-1 -right-1 h-5 w-5 fill-purple text-white" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate text-sm font-extrabold text-ink">{schoolContact.name}</p>
                    <span className="rounded-full bg-mint/35 px-2 py-0.5 text-[10px] font-extrabold text-ink">TERVERIFIKASI</span>
                  </div>
                  <p className="truncate text-xs font-semibold text-ink/60">{schoolContact.role} · {school.name}</p>
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 text-ink/40" aria-hidden="true" />
              </div>
            </button>
            
            {school.matchScore && (
              <div className="mt-6 pt-5 border-t-2 border-ink/10 flex items-center justify-center">
                <span className="inline-flex items-center gap-1 font-bold rounded-pill bg-[#E6F4EA] text-[#137333] border-2 border-[#137333]/20 px-3 py-1.5 text-sm">
                  <Sparkles size={16} className="text-[#137333]" />
                  {school.matchScore}% Match
                </span>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* BOTTOM: Similar Schools */}
      {similarSchools.length > 0 && (
        <div className="mt-20 pt-16 border-t-2 border-ink">
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-8">Sekolah Serupa</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {similarSchools.map((s) => (
              <SchoolCard key={s.id} school={s} />
            ))}
          </div>
        </div>
      )}



      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/95 backdrop-blur-md animate-fade-up">
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-6 right-6 text-warm/70 hover:text-warm p-2 transition-colors"
            aria-label="Tutup"
          >
            <X size={32} />
          </button>
          
          <div className="relative w-full max-w-6xl mx-auto px-4 sm:px-12 flex items-center justify-center">
            {images.length > 1 && (
              <button
                onClick={prevImage}
                className="absolute left-4 lg:-left-12 text-warm/50 hover:text-warm transition-colors"
              >
                <ChevronLeft size={48} />
              </button>
            )}
            
            <img
              src={images[activeImage]}
              alt="Expanded gallery"
              className="max-h-[85vh] w-auto max-w-full object-contain rounded-xl shadow-2xl"
            />
            
            {images.length > 1 && (
              <button
                onClick={nextImage}
                className="absolute right-4 lg:-right-12 text-warm/50 hover:text-warm transition-colors"
              >
                <ChevronRight size={48} />
              </button>
            )}
          </div>
          
          <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2">
            {images.map((_, idx) => (
              <div
                key={idx}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  idx === activeImage ? "bg-warm" : "bg-warm/30"
                }`}
              />
            ))}
          </div>
        </div>
      )}

      {consultationOpen && (
        <ConsultationModal
          school={school}
          contact={schoolContact}
          topics={consultationTopics}
          selectedTopic={consultationTopic}
          onSelectTopic={setConsultationTopic}
          onClose={() => setConsultationOpen(false)}
          onContinue={openWhatsApp}
        />
      )}
    </div>
  );
}

function ConsultationModal({ school, contact, topics, selectedTopic, onSelectTopic, onClose, onContinue }) {
  const activeTopic = topics.find((topic) => topic.id === selectedTopic) || topics[0];

  return (
    <div
      className="fixed inset-0 z-[110] flex items-end justify-center bg-ink/55 p-0 backdrop-blur-sm sm:items-center sm:p-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consultation-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-t-[28px] border-2 border-ink bg-warm p-5 shadow-offset sm:rounded-card sm:p-7">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-mint/30 px-3 py-1 text-xs font-extrabold text-ink">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" /> Kontak resmi sekolah
            </div>
            <h2 id="consultation-title" className="text-2xl font-black text-ink sm:text-3xl">Tanya langsung ke sekolah</h2>
            <p className="mt-1 text-sm font-medium text-ink/60">Pilih topik, lalu lanjutkan percakapan melalui WhatsApp.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-ink/15 p-2 text-ink/60 transition-colors hover:bg-cream hover:text-ink focus:outline-none focus-visible:ring-4 focus-visible:ring-purple/20"
            aria-label="Tutup dialog"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-6 overflow-hidden rounded-2xl border border-ink/15 bg-cream/65">
          <div className="flex items-center gap-4 p-4">
            <img src={contact.photo} alt={`Foto ${contact.name}`} className="h-16 w-16 shrink-0 rounded-2xl border-2 border-white object-cover shadow-sm" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-black text-ink">{contact.name}</p>
                <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-extrabold text-purple">
                  <BadgeCheck className="h-3.5 w-3.5 fill-purple text-white" /> Terverifikasi
                </span>
              </div>
              <p className="text-sm font-bold text-ink/65">{contact.role}</p>
              <p className="mt-1 truncate text-xs font-semibold text-ink/50">{school.name}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 border-t border-ink/10 bg-white/60 text-xs font-bold text-ink/65">
            <div className="flex items-center gap-2 border-r border-ink/10 px-3 py-3">
              <UserRoundCheck className="h-4 w-4 text-purple" /> Identitas dikonfirmasi
            </div>
            <div className="flex items-center gap-2 px-3 py-3">
              <Clock3 className="h-4 w-4 text-purple" /> {contact.responseTime.replace("Biasanya membalas dalam ", "± ")}
            </div>
          </div>
        </div>

        <fieldset>
          <legend className="mb-3 text-sm font-extrabold text-ink">Apa yang ingin ditanyakan?</legend>
          <div className="grid gap-2 sm:grid-cols-3">
            {topics.map((topic) => {
              const TopicIcon = topic.icon;
              const isActive = topic.id === selectedTopic;
              return (
                <button
                  key={topic.id}
                  type="button"
                  onClick={() => onSelectTopic(topic.id)}
                  className={`flex items-center gap-2 rounded-xl border-2 px-3 py-3 text-left text-xs font-extrabold transition-colors ${
                    isActive ? "border-ink bg-yellow text-ink" : "border-ink/15 bg-white text-ink/65 hover:border-ink/35"
                  }`}
                  aria-pressed={isActive}
                >
                  <TopicIcon className="h-4 w-4 shrink-0" /> {topic.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="my-5 rounded-2xl border border-ink/15 bg-white p-4">
          <p className="mb-2 text-[11px] font-extrabold uppercase tracking-wide text-ink/45">Pesan yang akan dikirim</p>
          <p className="text-sm font-medium leading-relaxed text-ink/75">{activeTopic.message}</p>
        </div>

        <Button onClick={onContinue} variant="mint" className="w-full py-3.5 text-base">
          <WhatsAppIcon className="h-5 w-5" /> Lanjut chat di WhatsApp
        </Button>
        <p className="mt-3 text-center text-[11px] font-medium text-ink/45">Anda akan diarahkan ke WhatsApp. Jangan membagikan data sensitif melalui chat.</p>
      </div>
    </div>
  );
}

function WhatsAppIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.04 2a9.84 9.84 0 0 0-8.44 14.9L2 22l5.24-1.55A9.96 9.96 0 1 0 12.04 2Zm0 17.95a8.03 8.03 0 0 1-4.1-1.12l-.3-.18-3.1.92.94-3.02-.2-.31a7.9 7.9 0 1 1 6.76 3.71Zm4.4-5.92c-.24-.12-1.43-.7-1.65-.79-.22-.08-.38-.12-.54.12-.16.24-.62.79-.76.95-.14.16-.28.18-.52.06-.24-.12-1.02-.37-1.94-1.2a7.18 7.18 0 0 1-1.34-1.67c-.14-.24-.02-.37.1-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.15.04-.3-.02-.42-.06-.12-.54-1.3-.74-1.78-.2-.47-.4-.4-.54-.41h-.46c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.32.98 2.48c.12.16 1.7 2.59 4.11 3.63.58.25 1.02.4 1.37.51.58.18 1.1.16 1.51.1.46-.07 1.43-.59 1.63-1.15.2-.56.2-1.04.14-1.14-.06-.1-.22-.16-.46-.28Z" />
    </svg>
  );
}

function Fact({ icon, label, value }) {
  return (
    <div className="bg-white border border-ink/20 rounded-2xl p-4 flex flex-col items-start gap-2">
      <div className="text-ink/60 bg-gray-50 p-2 rounded-xl border border-ink/20">
        {React.cloneElement(icon, { size: 20 })}
      </div>
      <div>
        <div className="text-ink/60 text-xs font-bold uppercase tracking-wide mb-0.5">{label}</div>
        <p className="font-bold leading-tight">{value}</p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-2xl font-extrabold mb-5 pb-3 border-b-2 border-ink inline-block">{title}</h2>
      <div>{children}</div>
    </section>
  );
}
